import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaUndo, FaPlus, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { LuCreditCard } from 'react-icons/lu';
import { useToast } from '../../../context/ToastContext';
import PageLayout from '../../../components/ui/PageLayout';
import PageHeader from '../../../components/ui/PageHeader';
import SkeletonLoader from '../../../components/ui/SkeletonLoader';
import PortalIconPicker from '../../../components/PortalCMS/PortalIconPicker';

const SLUG = 'pricing';

const emptyService = (order) => ({
  _id: `service_${Date.now()}`,
  name: '',
  basePrice: 0,
  popular: false,
  badgeLabel: '',
  badgeStyle: 'primary',
  frequency: 'one time',
  details: [''],
  hasCompanySelection: false,
  hasQuestions: false,
  hasHoursSelection: false,
  sliderPricingType: '',
  usesQuickPaymentApi: false,
  isActive: true,
  order,
});

const parsePriceInput = (raw) => {
  if (raw === '' || raw == null) return '';
  const parsed = parseFloat(String(raw));
  return Number.isFinite(parsed) ? parsed : '';
};

const BADGE_STYLE_OPTIONS = [
  { value: 'primary', label: 'Primary (blue)' },
  { value: 'success', label: 'Success (green)' },
  { value: 'amber', label: 'Amber (gold)' },
  { value: 'rose', label: 'Rose (red)' },
  { value: 'slate', label: 'Slate (gray)' },
];

const preventNumberWheel = (e) => e.currentTarget.blur();

const emptyQuestion = (id, formId = '') => ({ id, formId, text: '', price: 0, isActive: true });

const emptyTaxForm = (order) => ({
  id: `form_${Date.now()}`,
  name: '',
  description: '',
  order,
});

const defaultForm = () => ({
  isPublished: true,
  pageIcon: 'LuCreditCard',
  loadingMessage: '',
  popularBadgeLabel: '',
  buyNowLabel: '',
  loginToPurchaseLabel: '',
  alreadyPurchasedLabel: '',
  stepHeaders: {
    1: { title: '', subtitle: '' },
    1.1: { title: '', subtitle: '' },
    1.25: { title: '', subtitle: '' },
    1.5: { title: '', subtitle: '' },
    2: { title: '', subtitle: '' },
    3: { title: '', subtitle: '' },
  },
  stepTracker: {
    selectPlan: '',
    company: '',
    hours: '',
    taxDetails: '',
    location: '',
    checkout: '',
  },
  slider: {
    maxDots: 6,
    priceIncrement: 60,
    baseExpense: 20000,
    expenseIncrement: 15000,
    labelPrefix: '',
    labelSuffix: '',
  },
  services: [],
  taxTypes: {
    business: { title: '', subtitle: '', isActive: true, forms: [], questions: [] },
    personal: { title: '', subtitle: '', isActive: true, forms: [], questions: [] },
  },
  labels: {},
  companyFormation: {},
  hoursStep: {},
  taxStep: {},
  location: {},
  checkout: {},
  indianStates: [],
});

const PricingPortalCMS = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState('page');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [form, setForm] = useState(defaultForm());

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const c = res.data.data.content || {};
        const services = (c.services || []).map((item, i) => ({ ...item, order: item.order ?? i }));
        const hasSalesTax = services.some(s => s._id === 'service_10' || s.name?.toLowerCase().includes('sales tax'));
        if (!hasSalesTax) {
          services.push({
            _id: 'service_10',
            name: 'Sales Tax Module',
            basePrice: 40,
            popular: false,
            badgeLabel: '',
            badgeStyle: 'primary',
            frequency: 'per filing',
            details: [
              'Sales & Use Tax Calculation',
              'Multi-State Nexus Review',
              'State Filing Compliance',
              'Gross Receipts & Exemption Review',
              'Filing Confirmation & Records',
            ],
            hasCompanySelection: false,
            hasQuestions: false,
            hasHoursSelection: false,
            sliderPricingType: '',
            usesQuickPaymentApi: false,
            isActive: true,
            order: services.length,
          });
        }
        const ourFeeAmount = c.companyFormation?.ourFeeAmount;
        const formationIdx = services.findIndex((item) => item._id === 'service_1');
        if (formationIdx >= 0 && ourFeeAmount != null && ourFeeAmount !== '') {
          services[formationIdx] = { ...services[formationIdx], basePrice: Number(ourFeeAmount) };
        } else if (formationIdx >= 0 && services[formationIdx]?.basePrice != null) {
          c.companyFormation = { ...(c.companyFormation || {}), ourFeeAmount: Number(services[formationIdx].basePrice) };
        }
        setForm({
          ...defaultForm(),
          ...c,
          isPublished: res.data.data.isPublished !== false,
          stepHeaders: { ...defaultForm().stepHeaders, ...(c.stepHeaders || {}) },
          stepTracker: { ...defaultForm().stepTracker, ...(c.stepTracker || {}) },
          slider: { ...defaultForm().slider, ...(c.slider || {}) },
          taxTypes: {
            business: {
              ...defaultForm().taxTypes.business,
              ...(c.taxTypes?.business || {}),
              forms: (c.taxTypes?.business?.forms || []).map((form, i) => ({ ...form, order: form.order ?? i })),
              questions: c.taxTypes?.business?.questions || [],
            },
            personal: {
              ...defaultForm().taxTypes.personal,
              ...(c.taxTypes?.personal || {}),
              forms: (c.taxTypes?.personal?.forms || []).map((form, i) => ({ ...form, order: form.order ?? i })),
              questions: c.taxTypes?.personal?.questions || [],
            },
          },
          services,
          labels: { ...(c.labels || {}) },
          companyFormation: { ...(c.companyFormation || {}) },
          hoursStep: { ...(c.hoursStep || {}) },
          taxStep: { ...(c.taxStep || {}) },
          location: { ...(c.location || {}) },
          checkout: { ...(c.checkout || {}) },
          indianStates: c.indianStates || [],
        });
      }
    } catch {
      toastError('Failed to load pricing page content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateNested = (parent, field, value) =>
    setForm((prev) => ({ ...prev, [parent]: { ...prev[parent], [field]: value } }));
  const updateStepHeader = (step, field, value) =>
    setForm((prev) => ({
      ...prev,
      stepHeaders: { ...prev.stepHeaders, [step]: { ...prev.stepHeaders[step], [field]: value } },
    }));

  const moveService = (index, dir) => {
    setForm((prev) => {
      const list = [...prev.services];
      const next = index + dir;
      if (next < 0 || next >= list.length) return prev;
      [list[index], list[next]] = [list[next], list[index]];
      return { ...prev, services: list.map((item, i) => ({ ...item, order: i })) };
    });
  };

  const updateService = (index, field, value) => {
    setForm((prev) => {
      const list = [...prev.services];
      list[index] = { ...list[index], [field]: value };
      if (field === 'basePrice' && list[index]._id === 'service_1') {
        return {
          ...prev,
          services: list,
          companyFormation: { ...prev.companyFormation, ourFeeAmount: value },
        };
      }
      return { ...prev, services: list };
    });
  };

  const updateServiceDetail = (serviceIndex, detailIndex, value) => {
    setForm((prev) => {
      const list = [...prev.services];
      const details = [...(list[serviceIndex].details || [])];
      details[detailIndex] = value;
      list[serviceIndex] = { ...list[serviceIndex], details };
      return { ...prev, services: list };
    });
  };

  const addServiceDetail = (serviceIndex) => {
    setForm((prev) => {
      const list = [...prev.services];
      list[serviceIndex] = { ...list[serviceIndex], details: [...(list[serviceIndex].details || []), ''] };
      return { ...prev, services: list };
    });
  };

  const removeServiceDetail = (serviceIndex, detailIndex) => {
    setForm((prev) => {
      const list = [...prev.services];
      list[serviceIndex] = {
        ...list[serviceIndex],
        details: list[serviceIndex].details.filter((_, i) => i !== detailIndex),
      };
      return { ...prev, services: list };
    });
  };

  const updateTaxType = (type, field, value) =>
    setForm((prev) => ({
      ...prev,
      taxTypes: { ...prev.taxTypes, [type]: { ...prev.taxTypes[type], [field]: value } },
    }));

  const updateTaxQuestion = (type, index, field, value) => {
    setForm((prev) => {
      const questions = [...(prev.taxTypes[type].questions || [])];
      questions[index] = { ...questions[index], [field]: value };
      return { ...prev, taxTypes: { ...prev.taxTypes, [type]: { ...prev.taxTypes[type], questions } } };
    });
  };

  const addTaxQuestion = (type) => {
    setForm((prev) => {
      const questions = [...(prev.taxTypes[type].questions || [])];
      const defaultFormId = prev.taxTypes[type].forms?.[0]?.id || '';
      questions.push(emptyQuestion(`q${questions.length + 1}`, defaultFormId));
      return { ...prev, taxTypes: { ...prev.taxTypes, [type]: { ...prev.taxTypes[type], questions } } };
    });
  };

  const addTaxForm = (type) => {
    setForm((prev) => {
      const forms = [...(prev.taxTypes[type].forms || [])];
      forms.push(emptyTaxForm(forms.length));
      return { ...prev, taxTypes: { ...prev.taxTypes, [type]: { ...prev.taxTypes[type], forms } } };
    });
  };

  const updateTaxForm = (type, index, field, value) => {
    setForm((prev) => {
      const forms = [...(prev.taxTypes[type].forms || [])];
      forms[index] = { ...forms[index], [field]: value };
      return { ...prev, taxTypes: { ...prev.taxTypes, [type]: { ...prev.taxTypes[type], forms } } };
    });
  };

  const moveTaxForm = (type, index, dir) => {
    setForm((prev) => {
      const forms = [...(prev.taxTypes[type].forms || [])];
      const next = index + dir;
      if (next < 0 || next >= forms.length) return prev;
      [forms[index], forms[next]] = [forms[next], forms[index]];
      return {
        ...prev,
        taxTypes: {
          ...prev.taxTypes,
          [type]: { ...prev.taxTypes[type], forms: forms.map((form, i) => ({ ...form, order: i })) },
        },
      };
    });
  };

  const removeTaxForm = (type, index) => {
    setForm((prev) => {
      const forms = prev.taxTypes[type].forms.filter((_, i) => i !== index);
      const removedId = prev.taxTypes[type].forms[index]?.id;
      const fallbackId = forms[0]?.id || '';
      const questions = (prev.taxTypes[type].questions || []).map((question) =>
        question.formId === removedId ? { ...question, formId: fallbackId } : question
      );
      return {
        ...prev,
        taxTypes: {
          ...prev.taxTypes,
          [type]: { ...prev.taxTypes[type], forms, questions },
        },
      };
    });
  };

  const removeTaxQuestion = (type, index) => {
    setForm((prev) => {
      const questions = prev.taxTypes[type].questions.filter((_, i) => i !== index);
      return { ...prev, taxTypes: { ...prev.taxTypes, [type]: { ...prev.taxTypes[type], questions } } };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const formationIdx = form.services.findIndex((item) => item._id === 'service_1');
      const ourFeeAmount =
        form.companyFormation?.ourFeeAmount ??
        (formationIdx >= 0 ? form.services[formationIdx]?.basePrice : undefined);
      const syncedServices = form.services.map((item, i) => {
        const next = {
          ...item,
          details: (item.details || []).filter(Boolean),
          order: i,
        };
        if (item._id === 'service_1' && ourFeeAmount !== '' && ourFeeAmount != null) {
          next.basePrice = Number(ourFeeAmount);
        }
        return next;
      });
      const payload = {
        ...form,
        companyFormation: {
          ...form.companyFormation,
          ...(ourFeeAmount !== '' && ourFeeAmount != null ? { ourFeeAmount: Number(ourFeeAmount) } : {}),
        },
        services: syncedServices,
        taxTypes: {
          business: {
            ...form.taxTypes.business,
            forms: (form.taxTypes.business.forms || []).map((item, i) => ({ ...item, order: i })),
          },
          personal: {
            ...form.taxTypes.personal,
            forms: (form.taxTypes.personal.forms || []).map((item, i) => ({ ...item, order: i })),
          },
        },
      };
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`,
        { content: payload, isPublished: form.isPublished },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) toastSuccess('Pricing page saved');
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore preloaded pricing page defaults?')) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Defaults restored');
      fetchContent();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  const updateCompany = (field, value) =>
    setForm((prev) => {
      const companyFormation = { ...prev.companyFormation, [field]: value };
      if (field === 'ourFeeAmount') {
        const services = prev.services.map((item) =>
          item._id === 'service_1' ? { ...item, basePrice: value } : item
        );
        return { ...prev, companyFormation, services };
      }
      return { ...prev, companyFormation };
    });

  const tabs = [
    { id: 'page', label: 'Page & Steps' },
    { id: 'services', label: 'Service Cards' },
    { id: 'company', label: 'Company Formation' },
    { id: 'location', label: 'Location & Pay' },
    { id: 'checkout', label: 'Checkout' },
    { id: 'slider', label: 'Expense Slider' },
    { id: 'tax', label: 'Tax Filing' },
    { id: 'labels', label: 'Buttons & Labels' },
  ];

  if (loading) {
    return (
      <PageLayout>
        <SkeletonLoader count={4} />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title="Pricing Plan"
        description="Edit pricing service cards, step headers, slider, and tax add-on questions."
        icon={LuCreditCard}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={() => navigate('/portal-cms')} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 text-sm">Back</button>
            <button type="button" onClick={handleReset} disabled={resetting} className="px-4 py-2 rounded-xl border text-sm flex items-center gap-2">
              <FaUndo className="w-3.5 h-3.5" /> Reset
            </button>
            <button type="button" onClick={handleSave} disabled={saving} className="btn-premium text-sm py-2 px-4 flex items-center gap-2">
              <FaSave className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium ${activeTab === tab.id ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-dark-800 rounded-2xl border p-6 space-y-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.isPublished !== false} onChange={(e) => updateField('isPublished', e.target.checked)} />
          Published
        </label>

        {activeTab === 'page' && (
          <>
            <h3 className="font-bold pt-2">Main page</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <PortalIconPicker value={form.pageIcon} onChange={(icon) => updateField('pageIcon', icon)} />
              <input className="input-modern" value={form.loadingMessage} onChange={(e) => updateField('loadingMessage', e.target.value)} placeholder="Loading message" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input className="input-modern" value={form.popularBadgeLabel} onChange={(e) => updateField('popularBadgeLabel', e.target.value)} placeholder="Default badge text (when card badge is empty)" />
              <input className="input-modern" value={form.buyNowLabel} onChange={(e) => updateField('buyNowLabel', e.target.value)} placeholder="Buy now button" />
              <input className="input-modern" value={form.loginToPurchaseLabel} onChange={(e) => updateField('loginToPurchaseLabel', e.target.value)} placeholder="Login to purchase" />
              <input className="input-modern" value={form.alreadyPurchasedLabel} onChange={(e) => updateField('alreadyPurchasedLabel', e.target.value)} placeholder="Already purchased" />
            </div>

            <h3 className="font-bold pt-2">Step headers</h3>
            {Object.entries(form.stepHeaders).map(([step, header]) => (
              <div key={step} className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-xl bg-gray-50/50 dark:bg-dark-900/30">
                <span className="text-xs font-bold uppercase text-gray-500 md:col-span-2">Step {step}</span>
                <input className="input-modern" value={header.title} onChange={(e) => updateStepHeader(step, 'title', e.target.value)} placeholder="Title" />
                <input className="input-modern" value={header.subtitle} onChange={(e) => updateStepHeader(step, 'subtitle', e.target.value)} placeholder="Subtitle" />
              </div>
            ))}

            <h3 className="font-bold pt-2">Step tracker labels</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.keys(form.stepTracker).map((key) => (
                <input
                  key={key}
                  className="input-modern"
                  value={form.stepTracker[key]}
                  onChange={(e) => updateNested('stepTracker', key, e.target.value)}
                  placeholder={key}
                />
              ))}
            </div>
          </>
        )}

        {activeTab === 'company' && (
          <>
            <h3 className="font-bold">USA Company Formation step</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input className="input-modern" value={form.companyFormation.companyTypeTitle || ''} onChange={(e) => updateCompany('companyTypeTitle', e.target.value)} placeholder="Company type title" />
              <input className="input-modern" value={form.companyFormation.stateTitle || ''} onChange={(e) => updateCompany('stateTitle', e.target.value)} placeholder="State title" />
              <input className="input-modern" value={form.companyFormation.einTitle || ''} onChange={(e) => updateCompany('einTitle', e.target.value)} placeholder="EIN section title" />
              <input className="input-modern" value={form.companyFormation.registeredAgentIncluded || ''} onChange={(e) => updateCompany('registeredAgentIncluded', e.target.value)} placeholder="Registered agent banner" />
            </div>
            <textarea className="input-modern min-h-[80px]" value={(form.companyFormation.companyTypes || []).join('\n')} onChange={(e) => updateCompany('companyTypes', e.target.value.split('\n').filter(Boolean))} placeholder="Company types (one per line)" />
            <textarea className="input-modern min-h-[80px]" value={(form.companyFormation.usStates || []).join('\n')} onChange={(e) => updateCompany('usStates', e.target.value.split('\n').filter(Boolean))} placeholder="US states (one per line)" />
            <textarea className="input-modern min-h-[120px]" value={form.companyFormation.ssnQuestion || ''} onChange={(e) => updateCompany('ssnQuestion', e.target.value)} placeholder="SSN question" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <input className="input-modern" type="number" step="1" value={form.companyFormation.ourFeeAmount ?? ''} onChange={(e) => updateCompany('ourFeeAmount', parsePriceInput(e.target.value))} onWheel={preventNumberWheel} placeholder="Our fee ($)" />
              <input className="input-modern" type="number" step="1" value={form.companyFormation.einYesPrice ?? ''} onChange={(e) => updateCompany('einYesPrice', parsePriceInput(e.target.value))} onWheel={preventNumberWheel} placeholder="EIN yes price" />
              <input className="input-modern" type="number" step="1" value={form.companyFormation.einNoPrice ?? ''} onChange={(e) => updateCompany('einNoPrice', parsePriceInput(e.target.value))} onWheel={preventNumberWheel} placeholder="EIN no price" />
              <input className="input-modern" type="number" step="1" value={form.companyFormation.registeredAgentFee ?? ''} onChange={(e) => updateCompany('registeredAgentFee', parsePriceInput(e.target.value))} onWheel={preventNumberWheel} placeholder="Registered agent fee" />
            </div>
            <h4 className="font-semibold text-sm">Fee breakdown labels</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {['promoCodeTitle', 'promoCodePlaceholder', 'rewardPointsTitle', 'feesBreakdownTitle', 'ourFeeLabel', 'stateFeeLabel', 'einServiceLabel', 'registeredAgentLabel', 'promoDiscountLabel', 'rewardPointsApply', 'rewardPointsApplied'].map((key) => (
                <input key={key} className="input-modern" value={form.companyFormation[key] || ''} onChange={(e) => updateCompany(key, e.target.value)} placeholder={key} />
              ))}
            </div>
            <h4 className="font-semibold text-sm">Indian billing states (GST)</h4>
            <textarea className="input-modern min-h-[120px]" value={(form.indianStates || []).join('\n')} onChange={(e) => updateField('indianStates', e.target.value.split('\n').filter(Boolean))} placeholder="Indian states, one per line" />
          </>
        )}

        {activeTab === 'location' && (
          <>
            <h3 className="font-bold">Location / currency step</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {['sectionTitle', 'internationalTitle', 'internationalSubtitle', 'indiaTitle', 'indiaRatePrefix', 'selectStateLabel', 'selectStatePlaceholder'].map((key) => (
                <input key={key} className="input-modern" value={form.location?.[key] || ''} onChange={(e) => updateNested('location', key, e.target.value)} placeholder={key} />
              ))}
            </div>
            <h3 className="font-bold pt-2">Hours step</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {['title', 'hourlyRateLabel', 'hoursSelectedLabel', 'totalAmountLabel'].map((key) => (
                <input key={key} className="input-modern" value={form.hoursStep?.[key] || ''} onChange={(e) => updateNested('hoursStep', key, e.target.value)} placeholder={key} />
              ))}
            </div>
          </>
        )}

        {activeTab === 'checkout' && (
          <>
            <h3 className="font-bold">Order summary & invoice</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.keys(form.checkout || {}).map((key) => (
                <input key={key} className="input-modern" value={form.checkout?.[key] || ''} onChange={(e) => updateNested('checkout', key, e.target.value)} placeholder={key} />
              ))}
            </div>
          </>
        )}

        {activeTab === 'labels' && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {['back', 'continue', 'proceed', 'apply', 'applying', 'remove', 'clear', 'save', 'cancel', 'yes', 'no', 'total', 'subtotal', 'promoDiscount', 'referralDiscount'].map((key) => (
              <input key={key} className="input-modern" value={form.labels?.[key] || ''} onChange={(e) => updateNested('labels', key, e.target.value)} placeholder={key} />
            ))}
          </div>
        )}

        {activeTab === 'slider' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="input-modern" type="number" value={form.slider.maxDots} onChange={(e) => updateNested('slider', 'maxDots', Number(e.target.value))} placeholder="Max slider steps" />
            <input className="input-modern" type="number" value={form.slider.priceIncrement} onChange={(e) => updateNested('slider', 'priceIncrement', Number(e.target.value))} placeholder="Price increment per step" />
            <input className="input-modern" type="number" value={form.slider.baseExpense} onChange={(e) => updateNested('slider', 'baseExpense', Number(e.target.value))} placeholder="Base monthly expense" />
            <input className="input-modern" type="number" value={form.slider.expenseIncrement} onChange={(e) => updateNested('slider', 'expenseIncrement', Number(e.target.value))} placeholder="Expense increment per step" />
            <input className="input-modern" value={form.slider.labelPrefix} onChange={(e) => updateNested('slider', 'labelPrefix', e.target.value)} placeholder="Slider label prefix" />
            <input className="input-modern" value={form.slider.labelSuffix} onChange={(e) => updateNested('slider', 'labelSuffix', e.target.value)} placeholder="Slider label suffix" />
          </div>
        )}

        {activeTab === 'services' && (
          <>
            <div className="flex justify-between items-center">
              <h3 className="font-bold">Service cards</h3>
              <button type="button" onClick={() => setForm((prev) => ({ ...prev, services: [...prev.services, emptyService(prev.services.length)] }))} className="text-sm text-primary-600 flex items-center gap-1">
                <FaPlus /> Add card
              </button>
            </div>
            {form.services.map((service, index) => (
              <div key={service._id || index} className="rounded-2xl border p-5 space-y-3 bg-gray-50/50 dark:bg-dark-900/30">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold">Card {index + 1} — {service._id}</span>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => moveService(index, -1)} disabled={index === 0} className="p-2 rounded-lg border disabled:opacity-40"><FaArrowUp className="w-3 h-3" /></button>
                    <button type="button" onClick={() => moveService(index, 1)} disabled={index === form.services.length - 1} className="p-2 rounded-lg border disabled:opacity-40"><FaArrowDown className="w-3 h-3" /></button>
                    <button type="button" onClick={() => setForm((prev) => ({ ...prev, services: prev.services.filter((_, i) => i !== index) }))} className="p-2 rounded-lg text-red-500"><FaTrash className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input className="input-modern" value={service._id} onChange={(e) => updateService(index, '_id', e.target.value)} placeholder="Service ID" />
                  <input className="input-modern" value={service.name} onChange={(e) => updateService(index, 'name', e.target.value)} placeholder="Card title" />
                  <input className="input-modern" type="number" step="1" value={service.basePrice} onChange={(e) => updateService(index, 'basePrice', parsePriceInput(e.target.value))} onWheel={preventNumberWheel} placeholder="Base price ($)" />
                  <input className="input-modern" value={service.frequency} onChange={(e) => updateService(index, 'frequency', e.target.value)} placeholder="Frequency (e.g. per month)" />
                  <select className="input-modern" value={service.sliderPricingType || ''} onChange={(e) => updateService(index, 'sliderPricingType', e.target.value)}>
                    <option value="">Fixed price</option>
                    <option value="bookkeeping">Slider — Bookkeeping</option>
                    <option value="cfo">Slider — CFO</option>
                  </select>
                </div>
                <div className="rounded-xl border border-dashed border-primary-200 dark:border-primary-800/40 p-4 space-y-3 bg-primary-50/30 dark:bg-primary-900/10">
                  <h4 className="text-xs font-semibold uppercase text-primary-600 dark:text-primary-400">Card badge</h4>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={!!service.popular} onChange={(e) => updateService(index, 'popular', e.target.checked)} />
                      Show badge on card
                    </label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      className="input-modern"
                      value={service.badgeLabel || ''}
                      onChange={(e) => updateService(index, 'badgeLabel', e.target.value)}
                      placeholder={`Badge text (default: ${form.popularBadgeLabel || 'Most Popular'})`}
                      disabled={!service.popular}
                    />
                    <select
                      className="input-modern"
                      value={service.badgeStyle || 'primary'}
                      onChange={(e) => updateService(index, 'badgeStyle', e.target.value)}
                      disabled={!service.popular}
                    >
                      {BADGE_STYLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 text-sm">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={service.isActive !== false} onChange={(e) => updateService(index, 'isActive', e.target.checked)} /> Active</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={!!service.hasCompanySelection} onChange={(e) => updateService(index, 'hasCompanySelection', e.target.checked)} /> Company flow</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={!!service.hasQuestions} onChange={(e) => updateService(index, 'hasQuestions', e.target.checked)} /> Tax questions</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={!!service.hasHoursSelection} onChange={(e) => updateService(index, 'hasHoursSelection', e.target.checked)} /> Hours selection</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={!!service.usesQuickPaymentApi} onChange={(e) => updateService(index, 'usesQuickPaymentApi', e.target.checked)} /> Quick payment API</label>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase text-gray-500">Feature bullets</label>
                  {(service.details || []).map((detail, di) => (
                    <div key={di} className="flex gap-2">
                      <input className="input-modern flex-1" value={detail} onChange={(e) => updateServiceDetail(index, di, e.target.value)} />
                      <button type="button" onClick={() => removeServiceDetail(index, di)} className="p-2 text-red-500"><FaTrash className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addServiceDetail(index)} className="text-xs text-primary-600">+ Add bullet</button>
                </div>
              </div>
            ))}
          </>
        )}

        {activeTab === 'tax' && (
          <>
            <h3 className="font-bold">Tax filing step labels</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                'selectTaxTypeTitle',
                'additionalQuestionsTitle',
                'formQuestionsTitle',
                'priceSummaryTitle',
                'basePriceLabel',
                'businessTaxFilingLabel',
                'personalTaxFilingLabel',
                'addOnsLabel',
                'yesPriceSuffix',
                'noFormsFallbackLabel',
              ].map((key) => (
                <input
                  key={key}
                  className="input-modern"
                  value={form.taxStep?.[key] || ''}
                  onChange={(e) => updateNested('taxStep', key, e.target.value)}
                  placeholder={key}
                />
              ))}
            </div>

            {['business', 'personal'].map((type) => (
              <div key={type} className="space-y-4 pt-4 border-t border-gray-200 dark:border-dark-700">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="font-bold capitalize">{type} tax filing</h3>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.taxTypes[type]?.isActive !== false}
                      onChange={(e) => updateTaxType(type, 'isActive', e.target.checked)}
                    />
                    Show on user pricing page
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    className="input-modern"
                    value={form.taxTypes[type].title || ''}
                    onChange={(e) => updateTaxType(type, 'title', e.target.value)}
                    placeholder="Tax type card title (e.g. Form 1040)"
                  />
                  <input
                    className="input-modern"
                    value={form.taxTypes[type].subtitle || ''}
                    onChange={(e) => updateTaxType(type, 'subtitle', e.target.value)}
                    placeholder="Tax type card subtitle"
                  />
                </div>

                <div className="rounded-2xl border border-dashed border-primary-200 dark:border-primary-800/40 p-4 space-y-3 bg-primary-50/20 dark:bg-primary-900/10">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="text-sm font-semibold uppercase text-primary-600 dark:text-primary-400">Tax forms</h4>
                    <button type="button" onClick={() => addTaxForm(type)} className="text-sm text-primary-600 flex items-center gap-1">
                      <FaPlus /> Add form
                    </button>
                  </div>
                  {(form.taxTypes[type].forms || []).length === 0 && (
                    <p className="text-sm text-gray-500">No forms yet. Add a form (e.g. Form 1040, Form 1120S) then assign questions to it.</p>
                  )}
                  {(form.taxTypes[type].forms || []).map((formItem, fi) => (
                    <div key={formItem.id || fi} className="rounded-xl border p-4 space-y-3 bg-white/70 dark:bg-dark-900/40">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-bold uppercase text-gray-500">Form {fi + 1}</span>
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => moveTaxForm(type, fi, -1)} disabled={fi === 0} className="p-2 rounded-lg border disabled:opacity-40"><FaArrowUp className="w-3 h-3" /></button>
                          <button type="button" onClick={() => moveTaxForm(type, fi, 1)} disabled={fi === form.taxTypes[type].forms.length - 1} className="p-2 rounded-lg border disabled:opacity-40"><FaArrowDown className="w-3 h-3" /></button>
                          <button type="button" onClick={() => removeTaxForm(type, fi)} className="p-2 rounded-lg text-red-500"><FaTrash className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input className="input-modern" value={formItem.id || ''} onChange={(e) => updateTaxForm(type, fi, 'id', e.target.value)} placeholder="Form ID (e.g. form_1040)" />
                        <input className="input-modern" value={formItem.name || ''} onChange={(e) => updateTaxForm(type, fi, 'name', e.target.value)} placeholder="Form name (e.g. Form 1040)" />
                        <input className="input-modern md:col-span-2" value={formItem.description || ''} onChange={(e) => updateTaxForm(type, fi, 'description', e.target.value)} placeholder="Form description (optional)" />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="text-sm font-semibold uppercase text-gray-500">Form-wise questions</h4>
                    <button type="button" onClick={() => addTaxQuestion(type)} className="text-sm text-primary-600 flex items-center gap-1">
                      <FaPlus /> Add question
                    </button>
                  </div>
                  {(form.taxTypes[type].questions || []).map((q, qi) => (
                    <div key={q.id || qi} className="rounded-xl border p-4 space-y-3 bg-gray-50/50 dark:bg-dark-900/30">
                      <div className="grid grid-cols-1 md:grid-cols-[120px,1fr,100px,auto] gap-2 items-start">
                        <input className="input-modern" value={q.id || ''} onChange={(e) => updateTaxQuestion(type, qi, 'id', e.target.value)} placeholder="Question ID" />
                        <select
                          className="input-modern"
                          value={q.formId || ''}
                          onChange={(e) => updateTaxQuestion(type, qi, 'formId', e.target.value)}
                        >
                          <option value="">Select form</option>
                          {(form.taxTypes[type].forms || []).map((formItem) => (
                            <option key={formItem.id} value={formItem.id}>{formItem.name || formItem.id}</option>
                          ))}
                        </select>
                        <input
                          className="input-modern"
                          type="number"
                          step="1"
                          value={q.price ?? ''}
                          onChange={(e) => updateTaxQuestion(type, qi, 'price', parsePriceInput(e.target.value))}
                          onWheel={preventNumberWheel}
                          placeholder="Price ($)"
                        />
                        <button type="button" onClick={() => removeTaxQuestion(type, qi)} className="p-2 text-red-500"><FaTrash className="w-3.5 h-3.5" /></button>
                      </div>
                      <textarea
                        className="input-modern min-h-[72px]"
                        value={q.text || ''}
                        onChange={(e) => updateTaxQuestion(type, qi, 'text', e.target.value)}
                        placeholder="Question text shown to user"
                      />
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={q.isActive !== false} onChange={(e) => updateTaxQuestion(type, qi, 'isActive', e.target.checked)} />
                        Active (visible to users)
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default PricingPortalCMS;
